import { InnerBox } from "@/components/elements/InnerBox";
import { Grid, Stack, styled, Typography } from "@mui/material";
import { FC, useMemo } from "react";
import { JobFormTitle } from "@/features/job/components/job-form/JobFormTitle";
import { TJobForm } from "@/features/job/types/JobForm";
import { useEnum } from "@/api/services/global/enum";
import { JOB_GENDER_OPTIONS } from "@/features/job/constants/enum";
import { useGetSpecificSkillCertificateList } from "@/api/services/global/master";
import { JobInfoItem } from "@/features/job/components/job-detail/JobInfoItem";
import { findLabelByValue, findLabelsByValue } from "@/utils/object";
import { black, state } from "@/theme/colors";
import { FileItem } from "@/components/elements/FileItem";
import Divider from "@mui/material/Divider";
import { EMPTY_STR } from "@/constants/string";
import { NATIONALITY_ANY_VALUE, NATIONALITY_ANY_LABEL } from "@/constants/nationality";

type JobRequirementDetailProps = {
  defaultValues?: Partial<TJobForm>;
  isDraft?: boolean;
};

export const JobRequirementDetail: FC<JobRequirementDetailProps> = ({ defaultValues, isDraft }) => {
  const { data: japaneseLevelTypeOptions } = useEnum("EJapaneseLevelType");
  const { data: recruitmentLocationTypeOptions } = useEnum("ERecruitmentLocationType");
  const { data: jobResidenceStatusOptions } = useEnum("EJobResidenceStatus");
  const { isLoading: isLoadingGetSpecificSkillCertificate, getCertificatesByIndustryId } = useGetSpecificSkillCertificateList();

  const nationalitiesDisplay = useMemo(() => {
    const nationalityIds = defaultValues?.nationalityIds;
    const nationalities = defaultValues?.nationalities;

    if (nationalityIds?.includes(String(NATIONALITY_ANY_VALUE))) {
      return NATIONALITY_ANY_LABEL;
    }

    return nationalities?.map((item) => item.nationalityName).join("、");
  }, [defaultValues?.nationalityIds, defaultValues?.nationalities]);

  const getSpecificSkillCertificateLabels = () => {
    if (!isLoadingGetSpecificSkillCertificate && defaultValues?.companySpecificIndustryId) {
      const specificSkillCertificateList = getCertificatesByIndustryId(defaultValues?.companySpecificIndustryId);
      return findLabelsByValue(specificSkillCertificateList, defaultValues?.specificSkillCertificateIds ?? []);
    }
    return null;
  };

  return (
    <Stack gap={2}>
      <InnerBox title={<JobFormTitle text="応募要件" />} padding="24px" showDivider={false}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <JobInfoItem label="必要な日本語レベル" value={findLabelByValue(japaneseLevelTypeOptions, defaultValues?.japaneseLevelType)} />
          </Grid>
          <Grid item xs={6}>
            <JobInfoItem label="必要な日本語レベル（その他）" value={defaultValues?.japaneseLevelOther} />
          </Grid>
          <Grid item xs={12}>
            <JobInfoItem label="必要な技能・経験" value={defaultValues?.skillsExperience} type="textArea" />
          </Grid>
          <Grid item xs={12}>
            <JobInfoItem label="その他応募要件" value={defaultValues?.otherApplicationRequirements} type="textArea" />
          </Grid>
          <Grid item xs={6}>
            <JobInfoItem label="年齢制限（上限）" value={defaultValues?.ageLimit} />
          </Grid>
          <Grid item xs={6}>
            <JobInfoItem label="国籍" value={nationalitiesDisplay} required={!isDraft} />
          </Grid>
          <Grid item xs={6}>
            <JobInfoItem
              label="募集地"
              value={findLabelByValue(recruitmentLocationTypeOptions, defaultValues?.recruitmentLocationType)}
              required={!isDraft}
            />
          </Grid>
          <Grid item xs={6}>
            <JobInfoItem label="採用希望時期" value={defaultValues?.desiredHiringPeriod} />
          </Grid>
          <Grid item xs={12}>
            <JobInfoItem label="性別" value={findLabelByValue(JOB_GENDER_OPTIONS, defaultValues?.gender)} />
          </Grid>
          <Grid item xs={12}>
            <JobInfoItem
              label="在留資格"
              value={findLabelsByValue(jobResidenceStatusOptions, defaultValues?.residenceStatusIds ?? [])}
              required={!isDraft}
            />
          </Grid>
          <Grid item xs={12}>
            <JobInfoItem label="特定技能試験合格" value={getSpecificSkillCertificateLabels()} />
          </Grid>
          <Grid item xs={6}>
            <JobInfoItem
              label="求人票（URL）"
              value={defaultValues?.jobDocumentUrl}
              {...(defaultValues?.jobDocumentUrl ? { color: state.link_1, type: "link" } : {})}
            />
          </Grid>
          <Grid item xs={6}>
            <Stack gap={1}>
              <Typography variant="cap12Light" color={black[70]}>
                求人票（ファイル添付）
              </Typography>
              {defaultValues?.jobDocumentFilePaths?.length ? (
                <Stack direction="column" justifyContent="space-between" spacing={2}>
                  {defaultValues?.jobDocumentFilePaths?.map((path, index) => (
                    <FileItem
                      key={index}
                      currentFileIndex={index}
                      filePath={path}
                      files={defaultValues?.jobDocumentFilePaths?.map((item) => {
                        return {
                          filePath: item,
                        };
                      })}
                      downloadable
                      iconDownloadable={true}
                      previewEnabled
                      hideIcon={true}
                    />
                  ))}
                  <Divider />
                </Stack>
              ) : (
                <>
                  <Typography variant="body14">{EMPTY_STR.TEXT}</Typography>
                  <DividerWrapper />
                </>
              )}
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <JobInfoItem label="選考フロー" value={defaultValues?.selectionProcess} isShowDivider={false} />
          </Grid>
        </Grid>
      </InnerBox>
    </Stack>
  );
};

const DividerWrapper = styled(Divider)`
  margin-top: 8px;
  width: 100%;
`;
